import {
  GET_ACTIVE_PURCHASES,
  GET_MERGED_PASSES_BY_NAME,
  GET_ACTIVE_PURCHASES_WITH_ADDONS,
  HAS_PASSES_TO_SHOW,
  CHECK_IS_PURCHASED,
  SEPARATED_SUBSCRIPTIONS,
  GET_ADDONS,
  GET_REGULARS,
  GET_REGULARS_BY_ID,
  GET_ADDONS_BY_ID,
  GET_PREPARED_PACKAGES,
  GET_ALL_PURCHASES,
  GET_PREPARED_PACKAGES_BY_ID,
  GET_PASSES,
  GET_FILTERED_PASSES,
  GET_CURRENCY,
  GET_PARTICIPANTS_LIST,
  GET_PASSES_FILTERS_TAGS,
  ATTRIBUTES_BY_ID,
  GROUPED_ATTRIBUTES_BY_ID_MAP_GROUP_ALIAS,
} from '@/store/getterTypes';
import {
  PASS_OFFER_FIELD,
  CURRENCY_SYMBOLS,
  ORDER_ACTIVITY_STATUS, ORDER_STATUS,
  ORDER_TYPE,
  PASS_ACCESS_TYPE,
  PASS_TAG_FIELD,
} from '@/const';
import {combineFieldsByName} from '@/utils/helpers';
import {ALL_EVENTS, BUY, WATCH} from '@/pages';


export const checkPurchaseIsActive = (purchase) => {
  if (purchase.passType === ORDER_TYPE.subscription) {
    return !purchase.incomplete && purchase.hasOwnProperty('is_active') ?
      purchase.is_active :
      purchase.orders[0].activity_status === ORDER_ACTIVITY_STATUS.active;
  }
  if (
    purchase.passType === ORDER_TYPE.package ||
    purchase.passType === ORDER_TYPE.event
  ) {
    return purchase.orders[0].activity_status === ORDER_ACTIVITY_STATUS.active;
  }
};

const filterCancelledNotPaidOrders = (orders) => {
  return orders.filter((order) => !(order.activity_status === ORDER_ACTIVITY_STATUS.canceled &&
    order.status === ORDER_STATUS.created));
};

const separateRegularAndAddons = (passes = []) => {
  const regulars = [];
  const addons = [];
  passes.forEach((pass) => {
    if (pass.pass_type === PASS_ACCESS_TYPE.ADDITION) {
      addons.push(pass);
    } else {
      regulars.push(pass);
    }
  });
  return {regulars, addons};
};
const getPriceTitle = (pass) => {
  let period = '';
  if (pass.offer) {
    const periodCount = pass.period > 1 ? pass.period : '';
    period = `/${periodCount}${pass.offer}`;
  }
  const currency = pass.currency;
  return `${currency} ${CURRENCY_SYMBOLS[currency]}${pass.price}${period}`;
};

const getAdditionalFields = (pass, type) => {
  return {
    ...pass,
    buyLink: getBuyLink(type, pass.id),
    priceTitle: getPriceTitle(pass),
    key: `${type}-${pass.order_subscription_id || pass.id}`,
    passType: type,
  };
};

const getBuyLink = (type, id) => {
  return {
    name: BUY.name,
    query: {
      [`${type}_id`]: id,
    },
  };
};
const getWatchLink = (pass) => {
  if (pass.passType === ORDER_TYPE.event) {
    return {
      ...WATCH,
      params: {
        id: pass.id,
      },
    };
  }
  return ALL_EVENTS;
};

const checkTeamPassPurchased = ({pack, order, list}) => {
  const participantId = order.participant_id;

  participantId &&
    !pack.disabledParticipants.includes(participantId) &&
    pack.disabledParticipants.push(participantId);

  pack.purchased = list.length ===
      pack.disabledParticipants.length;
};

const checkAttributesPurchased = ({pack, attributeId}) => {
  pack.purchased = true;
  pack.preparedAttributes.list.forEach((attrGroup) => {
    if (attrGroup.multiattribute) {
      const hasAttrGroupId = Boolean(attributeId);
      attrGroup.purchased = !hasAttrGroupId || String(attributeId) === String(attrGroup.id);
      return;
    }
    attrGroup.purchased = true;
    attrGroup.values.forEach((attr) => {
      if (String(attributeId) === String(attr.id)) {
        attr.purchased = true;
      }
      if (!attr.purchased) {
        attrGroup.purchased = false;
      }
    });
    if (!attrGroup.purchased) {
      pack.purchased = false;
    }
  });
};

const checkCategoryPurchased = ({pack, categoryId, cb}) => {
  pack.purchased = true;
  pack.preparedCategories.forEach((categoryData) => {
    if (String(categoryData.id) === String(categoryId)) {
      categoryData.purchased = true;
      cb?.(categoryData);
    }
    if (!categoryData.purchased) {
      pack.purchased = false;
    }
  });
};

const checkPackagePurchased = ({order, pack, participantsList = []}) => {
  const categoryId = order.event_category_id;
  const attributeId = order.selected_attributes?.id;

  if (pack.is_team_pass) {
    return checkTeamPassPurchased({pack, order, list: participantsList});
  }
  if (pack.preparedAttributes.isAllMultiAttributes && pack.isMultiCategory) {
    pack.purchased = true;
    return;
  }
  if (pack.isMultiCategory) {
    checkAttributesPurchased({pack, attributeId});
    return;
  }
  if (pack.preparedAttributes.isAllMultiAttributes) {
    checkCategoryPurchased({pack, categoryId});
    return;
  }

  pack.purchased = true;
  checkCategoryPurchased({
    pack,
    categoryId,
    cb: (categoryData) => {
      checkAttributesPurchased({pack: categoryData, attributeId});
    },
  });
};

/**
 * Prepare attributes data for display in pass details
 * @param {object} passAttributes
 * @param {object} attributes
 * @param {string|number} passId
 * @return {object}
 */
function preparePassAttributes(passAttributes = {}, attributes= {}, passId) {
  const {
    byId: attributesById,
    byAlias: attributesByAlias,
  } = attributes;
  let isAllMultiAttributes = true;
  let list = [];

  if (passAttributes && Object.keys(passAttributes).length) {
    list = Object.entries(passAttributes)
        .map(([attrGroupId, attrGroupData]) => {
          const group = attributesById[attrGroupId];
          const multiattribute = Boolean(attrGroupData?.is_multi_attributes);

          if (!multiattribute) {
            isAllMultiAttributes = false;
          }

          return {
            name: group.name || '',
            id: String(attrGroupId),
            key: `${attrGroupId}-${passId}`,
            multiattribute,
            purchased: false,
            values: [...(attrGroupData?.values || [])].map((id) => ({
              id: String(id),
              key: String(id),
              name: attributesByAlias[group.alias]?.[id]?.title,
              purchased: false,
            })),
          };
        });
  }

  return {
    list,
    isAllMultiAttributes,
  };
}

/**
 * Prepare category data for display in pass details
 * @param {object} categories
 * @param {object} attributes
 * @param {string|number} passId
 * @return {array}
 */
function preparePassCategories(categories, attributes= {}, passId) {
  return Object.entries(categories || {})
      .sort(([_, aValue], [__, bValue]) => {
        return aValue.localeCompare(bValue);
      }).map(([id, name]) => {
        return {
          id,
          name,
          key: `${id}-${passId}`,
          purchased: false,
          preparedAttributes: JSON.parse(JSON.stringify(attributes)),
        };
      });
}
export default {
  [GET_MERGED_PASSES_BY_NAME]: (state, getters) => {
    const passes = getters[GET_PASSES];
    return combineFieldsByName({
      initArray: passes,
      resultFieldName: PASS_OFFER_FIELD,
      concatObjectByFieldName: PASS_TAG_FIELD,
    });
  },
  [HAS_PASSES_TO_SHOW]: (state, getters) => {
    return Boolean(getters[GET_PASSES].length);
  },
  [CHECK_IS_PURCHASED]: (state, getters) => (pass) => {
    const active = getters[GET_ACTIVE_PURCHASES];
    const participantsList = getters[GET_PARTICIPANTS_LIST];
    const attributes = {
      byId: getters[ATTRIBUTES_BY_ID],
      byAlias: getters[GROUPED_ATTRIBUTES_BY_ID_MAP_GROUP_ALIAS],
    };
    const preparedAttributes = preparePassAttributes(pass.attributes, attributes, pass.id);
    const preparedCategories = preparePassCategories(
        pass.event_categories,
        preparedAttributes,
        pass.id,
    );

    if (pass.passType === ORDER_TYPE.package) {
      const info = {
        purchased: false,
        disabledCategories: [],
        disabledParticipants: [],
        preparedAttributes,
        preparedCategories,
        is_team_pass: pass.is_team_pass,
        event_categories: pass.event_categories,
        isMultiCategory: Boolean(pass.is_multi_categories) ||
          !(pass.event_categories && Object.keys(pass.event_categories).length > 1),
      };
      active
          .filter((a) => a.passType === pass.passType && pass.id === a.id)
          .forEach((a) => {
            checkPackagePurchased({
              order: a,
              pack: info,
              participantsList,
            });
          });
      return info;
    } else {
      const purchased = !!active.find((a) => a.id === pass.id && a.passType === pass.passType);
      return {
        purchased: purchased,
        preparedAttributes: {...preparedAttributes, purchased},
        isMultiCategory: true,
      };
    }
  },
  [GET_ALL_PURCHASES]: (state, getters) => {
    return state.purchasesList
        .filter((p) => {
          if (p.orders) {
            return p.orders.length > 1 || p.orders[0].status !== ORDER_STATUS.created;
          }
          return p.order.status !== ORDER_STATUS.created;
        })
        .map(( {type, product, order = {}, orders = []} = {}) => {
          const purchase = {
            passType: type,
            ...(product || {}),
            orders: filterCancelledNotPaidOrders(orders.length ? orders : [order]),
            currency: getters[GET_CURRENCY],
          };

          const info = {
            name: purchase.orders[0].name,
            paused: purchase.orders[0].activity_status !== ORDER_ACTIVITY_STATUS.active,
            created: purchase.orders[0].created,
            price: purchase.orders[0].price,
            watchLink: getWatchLink(purchase),
            end_ts: purchase.orders[0].end_ts,
            is_trial: purchase.orders[0].is_trial || false,
            is_active: checkPurchaseIsActive(purchase),
            key: `${purchase.passType}_${purchase.orders[0].id}`,
            incomplete: Boolean(product.incomplete),
            paymentSystem: purchase.orders[0].payment_system_alias,
            tax: purchase.orders[0].tax,
          };

          return {
            ...info,
            ...purchase,
          };
        });
  },
  [GET_ACTIVE_PURCHASES]: (state, getters) => {
    return getters[GET_ALL_PURCHASES].filter(checkPurchaseIsActive);
  },
  [GET_PREPARED_PACKAGES]: (state, getters) => {
    return state.packagesList.map((pack) => {
      const pass = {
        ...getAdditionalFields({
          ...pack,
          currency: getters[GET_CURRENCY],
        }, ORDER_TYPE.package),
      };

      const purchase = getters[CHECK_IS_PURCHASED](pass);
      return {
        ...pass,
        ...purchase,
      };
    });
  },
  [GET_PREPARED_PACKAGES_BY_ID]: (state, getters) => {
    return Object.fromEntries(getters[GET_PREPARED_PACKAGES].map((pack) => [pack.id, pack]));
  },
  [SEPARATED_SUBSCRIPTIONS]: (state, getters) => separateRegularAndAddons(
      state.subscriptionsList.map((sub) => {
        const pass = {
          ...getAdditionalFields({
            ...sub,
            currency: getters[GET_CURRENCY],
          }, ORDER_TYPE.subscription),
        };
        return {
          ...pass,
          ...getters[CHECK_IS_PURCHASED](pass),
        };
      }),
  ),
  [GET_ADDONS]: (state, getters) => {
    const activeList = getters[GET_ACTIVE_PURCHASES];
    return getters[SEPARATED_SUBSCRIPTIONS].addons.map((addon) => {
      const purchase = activeList.find(
          (a) => addon.id === a.id &&
          a.passType === ORDER_TYPE.subscription &&
          a.pass_type === PASS_ACCESS_TYPE.ADDITION,
      );
      return {
        ...addon,
        ...(purchase || {}),
      };
    });
  },
  [GET_ADDONS_BY_ID]: (state, getters) => {
    return Object.fromEntries(getters[GET_ADDONS].map((addon) => [addon.id, addon]));
  },
  [GET_REGULARS]: (state, getters) => {
    const addonsList = getters[GET_ADDONS];

    return getters[SEPARATED_SUBSCRIPTIONS].regulars.map((regular) => {
      const addons = {
        purchased: [],
        available: [],
      };
      addonsList.forEach((addon) => {
        if (
          addon[PASS_TAG_FIELD] === regular[PASS_TAG_FIELD] &&
          addon.offer === regular.offer
        ) {
          addon.purchased ? addons.purchased.push(addon) : addons.available.push(addon);
        }
      });
      addons.all = [...addons.purchased, ...addons.available];

      return {
        ...regular,
        addons,
      };
    });
  },
  [GET_REGULARS_BY_ID]: (state, getters) => {
    return Object.fromEntries(getters[GET_REGULARS].map((reg) => [reg.id, reg]));
  },
  [GET_ACTIVE_PURCHASES_WITH_ADDONS]: (state, getters) => {
    const activePasses = getters[GET_ACTIVE_PURCHASES];
    const regulars = getters[GET_REGULARS_BY_ID];
    const addons = getters[GET_ADDONS_BY_ID];

    return activePasses.map((pass) => {
      if (pass.passType === ORDER_TYPE.subscription) {
        const subscription = (pass.pass_type === PASS_ACCESS_TYPE.REGULAR ?
          regulars[pass.id] : addons[pass.id]) || {};
        return {
          ...subscription,
          ...pass,
          key: `${ORDER_TYPE.subscription}_${pass.order_subscription_id}`,
        };
      } else {
        return {
          ...pass,
          key: `${pass.passType}_${pass.orders[0].id}`,
        };
      }
    });
  },
  [GET_PASSES]: (state, getters) => {
    return [
      ...getters[GET_REGULARS],
      ...getters[GET_PREPARED_PACKAGES],
    ].sort((a, b) => b.weight - a.weight);
  },
  [GET_FILTERED_PASSES]: (state, getters) => ({
    packages = [],
    subscriptions = [],
  } = {}) => {
    const allSubscriptions = {
      ...getters[GET_REGULARS_BY_ID],
    };
    const allPackages = getters[GET_PREPARED_PACKAGES_BY_ID];
    return [
      ...subscriptions.map(({id}) => allSubscriptions[id]).filter(Boolean),
      ...packages.map(({id}) => allPackages[id]).filter(Boolean),
    ].sort((a, b) => b.weight - a.weight);
  },
  [GET_PASSES_FILTERS_TAGS]: (state, g) => (combineField, list) => {
    return [...(list || g[GET_PASSES]).reduce((total, item) => {
      total.add(item[combineField]);
      return total;
    }, new Set())].sort();
  },
};
